import _ from 'lodash';

import { JSONValue } from 'src/app/graph/explorerPage/helpers/types';

type IntValue = {
  intValue: number;
};
type StringValue = {
  stringValue: number;
};
type BoolValue = {
  boolValue: number;
};
type ArrayValue = {
  arrayValue: AnyValue[];
};
type BytesValue = {
  bytesValue: Uint8Array;
};
type KeyValueListValue = {
  kvListValue: Record<string, AnyValue>;
};

type AnyValue =
  | IntValue
  | StringValue
  | BoolValue
  | ArrayValue
  | BytesValue
  | KeyValueListValue;

const isIntValue = (anyValue: unknown): anyValue is IntValue =>
  _.has(anyValue, 'intValue');
const isStringValue = (anyValue: unknown): anyValue is StringValue =>
  _.has(anyValue, 'stringValue');
const isBoolValue = (anyValue: unknown): anyValue is BoolValue =>
  _.has(anyValue, 'boolValue');
const isBytesValue = (anyValue: unknown): anyValue is BytesValue =>
  _.has(anyValue, 'bytesValue');
const isArrayValue = (anyValue: unknown): anyValue is ArrayValue =>
  _.has(anyValue, 'arrayValue');
const isKeyValueListValue = (
  anyValue: unknown,
): anyValue is KeyValueListValue => _.has(anyValue, 'kvListValue');

export const isAnyValue = (anyValue: unknown): anyValue is AnyValue =>
  isIntValue(anyValue) ||
  isStringValue(anyValue) ||
  isBoolValue(anyValue) ||
  isBytesValue(anyValue) ||
  isArrayValue(anyValue) ||
  isKeyValueListValue(anyValue);

/**
 * Transforms a JSON-encoded "AnyValue" to a native JSONValue.  Useful
 * for decoding OTLP attributes as described here:
 * https://opentelemetry.io/docs/specs/otel/common/attribute-type-mapping/
 */
export function fromAnyValue(value: unknown): JSONValue {
  if (isIntValue(value)) {
    return value.intValue;
  }
  if (isBoolValue(value)) {
    return value.boolValue;
  }
  if (isStringValue(value)) {
    return value.stringValue;
  }
  if (isBytesValue(value)) {
    return Buffer.from(value.bytesValue).toString('base64');
  }
  if (isArrayValue(value)) {
    return value.arrayValue.map((v: AnyValue) => fromAnyValue(v));
  }
  if (isKeyValueListValue(value)) {
    return _.transform(
      value.kvListValue,
      (transformed, v, k) => {
        // eslint-disable-next-line no-param-reassign
        transformed[k] = fromAnyValue(v);
      },
      Object.create(null),
    );
  }
  throw new Error('unsupported AnyValue');
}
